import { useState, useEffect, useCallback } from 'react';
import {
  Search, MapPin, Globe, Phone, Building2, CheckCircle,
  Loader2, ChevronRight, RefreshCw, Mail,
  PackageOpen, Send, ArrowUpDown, Car,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SearchResult {
  place_id: string;
  name: string;
  address: string;
  phone: string;
  website: string;
  // cross-referenced from pipeline
  pipelineId?: string;
  pipelineStatus?: PipelineStatus;
  vehicleCount?: number;
}

type PipelineStatus = 'prospect' | 'imported' | 'contacted' | 'signed' | 'boosted';

interface PipelineDealer {
  id: string;
  dealerId: string | null;
  name: string;
  address: string;
  phone: string;
  website: string;
  email: string | null;
  contactEmail: string | null;   // from dealers table JOIN
  contactPhone: string | null;   // from dealers table JOIN
  searchZip: string;
  distanceMiles: number | null;
  status: PipelineStatus;
  vehicleCount: number;
  outreachSentAt: string | null;
  vehiclesImported: number | null;
  vehiclesFailed: number | null;
  importStatus: string | null;
}

interface PipelineCounts {
  prospect: number;
  imported: number;
  contacted: number;
  signed: number;
  boosted: number;
}

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<PipelineStatus, { label: string; color: string; bg: string }> = {
  prospect:  { label: 'Prospect',  color: 'text-gray-600',   bg: 'bg-gray-100'   },
  imported:  { label: 'Imported',  color: 'text-blue-700',   bg: 'bg-blue-100'   },
  contacted: { label: 'Contacted', color: 'text-amber-700',  bg: 'bg-amber-100'  },
  signed:    { label: 'Signed',    color: 'text-green-700',  bg: 'bg-green-100'  },
  boosted:   { label: 'Boosted',   color: 'text-purple-700', bg: 'bg-purple-100' },
};

// ─── Zip Search View ──────────────────────────────────────────────────────────

function ZipSearchView({ onAdded }: { onAdded: () => void }) {
  const [zip, setZip] = useState('');
  const [zipError, setZipError] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [pipeline, setPipeline] = useState<PipelineDealer[]>([]);
  const [adding, setAdding] = useState<Record<string, boolean>>({});
  const [importing, setImporting] = useState<Record<string, boolean>>({});
  const [importResults, setImportResults] = useState<Record<string, { imported: number; failed: number }>>({});
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  // Load pipeline once for cross-reference
  useEffect(() => {
    fetch('/api/dealer-prospecting')
      .then(r => r.json())
      .then((d: { pipeline: PipelineDealer[] }) => setPipeline(d.pipeline ?? []))
      .catch(() => {});
  }, []);

  const handleSearch = async () => {
    const clean = zip.trim();
    if (!/^\d{5}$/.test(clean)) { setZipError('Enter a valid 5-digit zip code'); return; }
    setZipError('');
    setSearching(true);
    setResults([]);
    try {
      const r = await fetch(`/api/search-dealers-by-zip?zip=${clean}`);
      const d = await r.json() as { dealers: SearchResult[]; error?: string };
      if (!r.ok) throw new Error(d.error ?? 'Search failed');

      // Cross-reference with pipeline
      const enriched = (d.dealers ?? []).map(dealer => {
        const match = pipeline.find(p =>
          p.name.toLowerCase() === dealer.name.toLowerCase() ||
          (dealer.website && p.website === dealer.website)
        );
        return match
          ? { ...dealer, pipelineId: match.id, pipelineStatus: match.status, vehicleCount: match.vehicleCount }
          : dealer;
      });
      setResults(enriched);
    } catch (e) {
      setZipError(e instanceof Error ? e.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleAddProspect = async (dealer: SearchResult) => {
    setAdding(prev => ({ ...prev, [dealer.place_id]: true }));
    try {
      const r = await fetch('/api/dealer-prospecting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'add_prospect',
          name: dealer.name,
          address: dealer.address,
          phone: dealer.phone,
          website: dealer.website,
          searchZip: zip,
        }),
      });
      const d = await r.json() as { pipelineId?: string; warning?: string; existingId?: string };

      if (d.warning === 'possible_duplicate') {
        showToast(`⚠️ Possible duplicate of existing pipeline entry`);
        return;
      }
      if (d.pipelineId) {
        setResults(prev => prev.map(res =>
          res.place_id === dealer.place_id
            ? { ...res, pipelineId: d.pipelineId, pipelineStatus: 'prospect' as PipelineStatus }
            : res
        ));
        showToast(`${dealer.name} added to pipeline`);
        onAdded();
      }
    } catch {
      showToast('Failed to add prospect');
    } finally {
      setAdding(prev => ({ ...prev, [dealer.place_id]: false }));
    }
  };

  const handleImport = async (dealer: SearchResult) => {
    if (!dealer.pipelineId) return;
    setImporting(prev => ({ ...prev, [dealer.place_id]: true }));
    try {
      const r = await fetch('/api/dealer-prospecting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'import_inventory', pipelineId: dealer.pipelineId }),
      });
      const d = await r.json() as { status: string; vehiclesImported?: number; vehiclesFailed?: number; fallbackOptions?: string[] };

      if (d.status === 'no_vehicles' || d.status === 'no_website') {
        showToast(`No vehicles found — use manual VIN or CSV import`);
      } else if (d.status === 'complete') {
        setImportResults(prev => ({ ...prev, [dealer.place_id]: { imported: d.vehiclesImported ?? 0, failed: d.vehiclesFailed ?? 0 } }));
        setResults(prev => prev.map(res =>
          res.place_id === dealer.place_id ? { ...res, pipelineStatus: 'imported' as PipelineStatus } : res
        ));
        showToast(`✅ ${d.vehiclesImported} vehicles imported for ${dealer.name}`);
        onAdded();
      }
    } catch {
      showToast('Import failed — check dealer website URL');
    } finally {
      setImporting(prev => ({ ...prev, [dealer.place_id]: false }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Search input */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Search Dealers by Zip Code</h3>
        <div className="flex gap-3">
          <div className="flex-1">
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={zip}
                onChange={e => { setZip(e.target.value); setZipError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Enter zip code (e.g. 30303)"
                maxLength={5}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            {zipError && <p className="mt-1 text-xs text-red-600">{zipError}</p>}
          </div>
          <button
            onClick={handleSearch}
            disabled={searching}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {searching ? 'Searching…' : 'Search'}
          </button>
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{results.length} dealers found near {zip}</p>
          {results.map(dealer => {
            const inFam = !!dealer.pipelineId;
            const isAdding = adding[dealer.place_id];
            const isImporting = importing[dealer.place_id];
            const importResult = importResults[dealer.place_id];
            const status = dealer.pipelineStatus;
            const cfg = status ? STATUS_CONFIG[status] : null;

            return (
              <div key={dealer.place_id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-sm">{dealer.name}</span>
                      {inFam && cfg && (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                          {cfg.label}{status === 'imported' && dealer.vehicleCount ? ` · ${dealer.vehicleCount} vehicles` : ''}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {dealer.address && <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{dealer.address}</p>}
                      {dealer.phone   && <p className="text-xs text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{dealer.phone}</p>}
                      {dealer.website && (
                        <a href={dealer.website} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 flex items-center gap-1 hover:underline">
                          <Globe className="w-3 h-3" />{dealer.website.replace(/^https?:\/\//, '')}
                        </a>
                      )}
                    </div>
                    {importResult && (
                      <p className="mt-2 text-xs text-green-700 bg-green-50 rounded-lg px-2 py-1">
                        ✅ {importResult.imported} imported{importResult.failed > 0 ? `, ${importResult.failed} failed` : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end shrink-0">
                    {!inFam && (
                      <button
                        onClick={() => handleAddProspect(dealer)}
                        disabled={isAdding}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                      >
                        {isAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronRight className="w-3 h-3" />}
                        Add to Pipeline
                      </button>
                    )}
                    {inFam && status === 'prospect' && (
                      <button
                        onClick={() => handleImport(dealer)}
                        disabled={isImporting}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {isImporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackageOpen className="w-3 h-3" />}
                        {isImporting ? 'Importing…' : 'Import Inventory'}
                      </button>
                    )}
                    {inFam && status === 'imported' && (
                      <span className="flex items-center gap-1 text-xs text-green-700">
                        <CheckCircle className="w-3.5 h-3.5" /> Inventory imported
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-xl shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

// ─── Pipeline View ────────────────────────────────────────────────────────────

function PipelineView({ refreshKey }: { refreshKey: number }) {
  const [pipeline, setPipeline] = useState<PipelineDealer[]>([]);
  const [counts, setCounts] = useState<PipelineCounts>({ prospect: 0, imported: 0, contacted: 0, signed: 0, boosted: 0 });
  const [statusFilter, setStatusFilter] = useState<PipelineStatus | 'all'>('all');
  const [sort, setSort] = useState<'recency' | 'vehicles'>('recency');
  const [loading, setLoading] = useState(true);
  const [outreachModal, setOutreachModal] = useState<PipelineDealer | null>(null);
  const [outreachEmail, setOutreachEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [importing, setImporting] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState('');
  const [inviteModal, setInviteModal] = useState<PipelineDealer | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const r = await fetch(`/api/dealer-prospecting?${params}`);
      const d = await r.json() as { pipeline: PipelineDealer[]; counts: PipelineCounts };
      setPipeline(d.pipeline ?? []);
      setCounts(d.counts ?? { prospect: 0, imported: 0, contacted: 0, signed: 0, boosted: 0 });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sort]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleSendOutreach = async () => {
    if (!outreachModal) return;
    const email = outreachEmail.trim() || outreachModal.email || '';
    if (!email) { showToast('Enter a recipient email address'); return; }
    setSending(true);
    try {
      const r = await fetch('/api/dealer-prospecting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'send_outreach', pipelineId: outreachModal.id, recipientEmail: email }),
      });
      const d = await r.json() as { status?: string; error?: string };
      if (d.status === 'sent') {
        showToast(`✅ Outreach sent to ${email}`);
        setOutreachModal(null);
        load();
      } else {
        showToast(`Failed: ${d.error ?? 'Unknown error'}`);
      }
    } finally {
      setSending(false);
    }
  };

  const handleImport = async (dealer: PipelineDealer) => {
    setImporting(prev => ({ ...prev, [dealer.id]: true }));
    try {
      const r = await fetch('/api/dealer-prospecting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'import_inventory', pipelineId: dealer.id }),
      });
      const d = await r.json() as { status: string; vehiclesImported?: number; vehiclesFailed?: number };
      if (d.status === 'no_vehicles' || d.status === 'no_website') {
        showToast(`No vehicles found — check dealer website URL`);
      } else if (d.status === 'complete') {
        showToast(`✅ ${d.vehiclesImported} vehicles imported for ${dealer.name}`);
        load();
      } else {
        showToast(`Import returned: ${d.status}`);
      }
    } catch {
      showToast('Import failed — check dealer website URL');
    } finally {
      setImporting(prev => ({ ...prev, [dealer.id]: false }));
    }
  };

  const handleSendInvite = async () => {
    if (!inviteModal) return;
    setSendingInvite(true);
    try {
      const r = await fetch('/api/dealer-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealerId: inviteModal.dealerId,
          dealerEmail: inviteEmail || inviteModal.contactEmail || inviteModal.email || '',
          message: inviteMessage || undefined,
          adminUserId: 'admin',
        }),
      });
      const d = await r.json() as { ok?: boolean; error?: string };
      if (d.ok) {
        showToast(`✅ Invite sent to ${inviteModal.name}`);
        setInviteModal(null);
        setInviteEmail('');
        setInviteMessage('');
      } else {
        showToast(`Failed: ${d.error ?? 'Unknown error'}`);
      }
    } finally {
      setSendingInvite(false);
    }
  };

  const STATUS_TABS: Array<{ id: PipelineStatus | 'all'; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'prospect', label: 'Prospect' },
    { id: 'imported', label: 'Imported' },
    { id: 'contacted', label: 'Contacted' },
    { id: 'signed', label: 'Signed' },
    { id: 'boosted', label: 'Boosted' },
  ];

  return (
    <div className="space-y-4">
      {/* Summary counts */}
      <div className="grid grid-cols-5 gap-3">
        {(Object.keys(STATUS_CONFIG) as PipelineStatus[]).map(s => (
          <div key={s} className="bg-white rounded-xl border border-gray-200 p-3 text-center">
            <p className="text-2xl font-bold text-gray-900">{counts[s]}</p>
            <p className={`text-xs font-medium mt-0.5 ${STATUS_CONFIG[s].color}`}>{STATUS_CONFIG[s].label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {STATUS_TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setStatusFilter(t.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                statusFilter === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}{t.id !== 'all' && counts[t.id as PipelineStatus] > 0 ? ` (${counts[t.id as PipelineStatus]})` : ''}
            </button>
          ))}
        </div>
        <button
          onClick={() => setSort(s => s === 'recency' ? 'vehicles' : 'recency')}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
        >
          <ArrowUpDown className="w-3.5 h-3.5" />
          Sort: {sort === 'recency' ? 'Recent' : 'Vehicles'}
        </button>
      </div>

      {/* Pipeline list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
        </div>
      ) : pipeline.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <Building2 className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No dealers in this stage yet.</p>
          <p className="text-xs text-gray-400 mt-1">Use the Zip Search tab to discover and add dealers.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pipeline.map(dealer => {
            const cfg = STATUS_CONFIG[dealer.status];
            return (
              <div key={dealer.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 text-sm">{dealer.name}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                        {cfg.label}
                      </span>
                      {dealer.vehicleCount > 0 && (
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Car className="w-3 h-3" />{dealer.vehicleCount} vehicles
                        </span>
                      )}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {dealer.address && <p className="text-xs text-gray-500">{dealer.address}</p>}
                      {(dealer.contactEmail || dealer.email) && (
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />{dealer.contactEmail ?? dealer.email}
                        </p>
                      )}
                      {dealer.contactPhone && (
                        <a href={`tel:${dealer.contactPhone}`} className="text-xs text-gray-500 flex items-center gap-1 hover:text-indigo-600">
                          <Phone className="w-3 h-3" />{dealer.contactPhone}
                        </a>
                      )}
                      {dealer.website && (
                        <a href={dealer.website} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline flex items-center gap-1">
                          <Globe className="w-3 h-3" />{dealer.website.replace(/^https?:\/\//, '')}
                        </a>
                      )}
                      {dealer.outreachSentAt && (
                        <p className="text-xs text-amber-600">Outreach sent {new Date(dealer.outreachSentAt).toLocaleDateString()}</p>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 flex flex-col gap-2 items-end">
                    {dealer.status === 'prospect' && dealer.vehicleCount === 0 && (
                      <button
                        onClick={() => handleImport(dealer)}
                        disabled={importing[dealer.id]}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {importing[dealer.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <PackageOpen className="w-3 h-3" />}
                        {importing[dealer.id] ? 'Importing…' : 'Import Inventory'}
                      </button>
                    )}
                    {dealer.status === 'prospect' && dealer.vehicleCount > 0 && (
                      <span className="flex items-center gap-1 text-xs text-blue-600 font-medium">
                        <CheckCircle className="w-3.5 h-3.5" /> Imported ({dealer.vehicleCount})
                      </span>
                    )}
                    {dealer.status === 'imported' && (
                      <button
                        onClick={() => { setOutreachModal(dealer); setOutreachEmail(dealer.email ?? ''); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-xs font-medium rounded-lg hover:bg-amber-600 transition-colors"
                      >
                        <Send className="w-3 h-3" /> Send Outreach
                      </button>
                    )}
                    {dealer.status === 'contacted' && (
                      <button
                        onClick={() => { setOutreachModal(dealer); setOutreachEmail(dealer.email ?? ''); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <RefreshCw className="w-3 h-3" /> Re-send
                      </button>
                    )}
                    {(dealer.status === 'signed' || dealer.status === 'boosted') && (
                      <span className="flex items-center gap-1 text-xs text-green-700">
                        <CheckCircle className="w-3.5 h-3.5" /> {dealer.status === 'boosted' ? 'Boosted' : 'Signed up'}
                      </span>
                    )}
                    {/* Invite button — available for any status */}
                    <button
                      onClick={() => setInviteModal(dealer)}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-indigo-300 text-indigo-600 text-xs font-medium rounded-lg hover:bg-indigo-50 transition-colors"
                    >
                      <Mail className="w-3 h-3" /> Invite
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Outreach modal */}
      {outreachModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-base font-bold text-gray-900 mb-1">Send Outreach to {outreachModal.name}</h3>
            <p className="text-sm text-gray-500 mb-4">
              Sends a branded FAM email with the value proposition + dealer sign-up link.
            </p>
            <label className="block text-xs font-medium text-gray-700 mb-1">Dealer email address</label>
            <input
              type="email"
              value={outreachEmail}
              onChange={e => setOutreachEmail(e.target.value)}
              placeholder="owner@dealership.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setOutreachModal(null)}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendOutreach}
                disabled={sending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite modal */}
      {inviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-base font-bold text-gray-900 mb-1">Invite {inviteModal.name}</h3>
            <p className="text-sm text-gray-500 mb-4">
              Send a direct invite link so this dealer can claim their profile and complete sign-up on FAM.
            </p>
            <label className="block text-xs font-medium text-gray-700 mb-1">Dealer email address</label>
            <input
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              placeholder={inviteModal.contactEmail ?? inviteModal.email ?? 'owner@dealership.com'}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 mb-3"
            />
            <label className="block text-xs font-medium text-gray-700 mb-1">Optional message</label>
            <textarea
              value={inviteMessage}
              onChange={e => setInviteMessage(e.target.value)}
              placeholder="Add a personal note…"
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 mb-4 resize-none"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setInviteModal(null); setInviteEmail(''); setInviteMessage(''); }}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvite}
                disabled={sendingInvite}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {sendingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {sendingInvite ? 'Sending…' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-xl shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

// ─── Claim Requests View ──────────────────────────────────────────────────────

function ClaimRequestsView() {
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Dealer Claim Requests</h3>
        <p className="text-sm text-gray-500 mb-4">
          When a dealer visits the public dealer page and clicks "Claim this profile", a claim request is created.
          Pending requests appear here for admin review before granting access.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <p className="text-xs font-mono text-gray-600 mb-1 font-semibold">To view pending claim requests, run:</p>
          <p className="text-xs font-mono text-gray-500 bg-gray-100 rounded px-3 py-2 select-all">
            SELECT * FROM dealer_claim_requests WHERE status='pending' ORDER BY created_at DESC;
          </p>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Full claim request management UI coming soon — approve or reject requests directly from this panel.
        </p>
      </div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

type PanelTab = 'search' | 'pipeline' | 'claims';

export function DealerProspectingPanel() {
  const [activeTab, setActiveTab] = useState<PanelTab>('search');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleAdded = () => setRefreshKey(k => k + 1);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Dealer Prospecting</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Find local dealers, import their inventory, and send outreach to build FAM's supply.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {([
          { id: 'search' as PanelTab, label: '🔍 Zip Search' },
          { id: 'pipeline' as PanelTab, label: '📋 Pipeline' },
          { id: 'claims' as PanelTab, label: '📋 Claim Requests' },
        ]).map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === t.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'claims'
        ? <ClaimRequestsView />
        : activeTab === 'search'
        ? <ZipSearchView onAdded={handleAdded} />
        : <PipelineView refreshKey={refreshKey} />
      }
    </div>
  );
}
